import { components as c } from "@/components";
import { formComponents as fc } from "@/components/form";
import { Label } from "@/components/ui/label";
import useInjectionOptions from "@/features/injection/hooks/useInjectionOptions";
import InviteForm from "@/features/invite/components/InviteForm";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { isUuid } from "@/services/isUuid";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const injectionFormSchema = z.object({
	referring_clinician_id: z.string().uuid().optional().nullable(),
	injecting_clinician_id: z.string().uuid().optional().nullable(),
	injection_method_id: z.string().uuid().optional().nullable(),
});

type InjectionFormValues = z.infer<typeof injectionFormSchema>;

const InjectionFormFieldsPage = () => {
	const auth = useAuth();

	const {
		referring_clinician_id,
		injecting_clinician_id,
		injection_method_id,
		organisation_id,
		setReferringClinicianId,
		setInjectingClinicianId,
		setInjectionMethodId,
	} = useTemplateStore();

	const {
		markStepComplete,
		nextStep,
		setCurrentStep,
		getNextStepRoute,
		getPreviousStepRoute,
		currentStep,
	} = useTemplateNavigationStore();
	const navigate = useNavigate();
	const { templateId } = Route.useParams();

	useEffect(() => {
		setCurrentStep("injection");
	}, [setCurrentStep]);

	const form = useForm<InjectionFormValues>({
		resolver: zodResolver(injectionFormSchema),
		defaultValues: {
			referring_clinician_id: isUuid(referring_clinician_id)
				? referring_clinician_id
				: auth?.id,
			injecting_clinician_id: isUuid(injecting_clinician_id)
				? injecting_clinician_id
				: auth?.id,
			injection_method_id,
		},
	});

	const { methodOptions, clinicianOptions } = useInjectionOptions(
		organisation_id,
	);

	useEffect(() => {
		const subscription = form.watch((value) => {
			setReferringClinicianId(value?.referring_clinician_id ?? null);
			setInjectingClinicianId(value?.injecting_clinician_id ?? null);
			setInjectionMethodId(value?.injection_method_id ?? null);
		});
		return () => subscription.unsubscribe();
	}, [
		form,
		setReferringClinicianId,
		setInjectingClinicianId,
		setInjectionMethodId,
	]);

	const handleNext = () => {
		markStepComplete(currentStep);
		nextStep();
		const nextRoute = getNextStepRoute(currentStep, { templateId });
		if (nextRoute) navigate(nextRoute);
	};

	const handlePrevious = () => {
		const prevRoute = getPreviousStepRoute(currentStep, { templateId });
		if (prevRoute) navigate(prevRoute);
	};

	return (
		<c.ui.Form {...form}>
			<form onSubmit={form.handleSubmit(handleNext)}>
				<div className="space-y-6">
					<div>
						{/* Referrer */}
						<Label htmlFor="referring_clinician_id">
							Referring Clinician
						</Label>
						<fc.ComboBox
							autoFocus
							name="referring_clinician_id"
							description="The clinician who requests the injection"
							options={clinicianOptions}
							clearable={true}
							addOption={<InviteForm />}
						/>
					</div>
					<div>
						{/* Injector */}
						<Label htmlFor="injecting_clinician_id">
							Injecting Clinician
						</Label>
						<fc.ComboBox
							name="injecting_clinician_id"
							description="The clinician who performs the injection"
							options={clinicianOptions}
							clearable={true}
							addOption={<InviteForm />}
						/>
					</div>
					<div>
						{/* Method */}
						<Label htmlFor="injection_method_id">
							Injection Method
						</Label>
						<fc.ComboBox
							name="injection_method_id"
							description="How and where injection is performed"
							options={methodOptions}
							clearable={true}
						/>
					</div>
				</div>

				{/* Navigation Buttons */}
				<div className="flex justify-between gap-4 mt-8">
					<c.ui.Button
						type="button"
						variant="outline"
						onClick={handlePrevious}
					>
						Previous
					</c.ui.Button>

					<c.ui.Button type="submit">
						Next
					</c.ui.Button>
				</div>
			</form>
		</c.ui.Form>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$templateId/events/injection/",
)({
	component: InjectionFormFieldsPage,
});
